import { Capacitor } from "@capacitor/core";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

/**
 * Generic client-side file download — the mechanism CSV and JSON exports
 * both need. The web `<a download>` + blob-URL trick (still used below)
 * silently fails inside a Capacitor WKWebView (backlog MOBILE-7, confirmed
 * via device log): the OS has no LaunchServices handler for a `blob:` URL
 * outside a real browser, so the click just dies with no visible error.
 * Native writes the file for real (Filesystem) and hands it to the OS share
 * sheet (Share) instead — the only way to actually get a file off-device
 * from a native shell.
 */
export async function triggerDownload(filename: string, blob: Blob): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { uri } = await Filesystem.writeFile({
      path: filename,
      data: await blob.text(),
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
    });
    try {
      await Share.share({ files: [uri] });
    } catch (e) {
      // The user backing out of the native share sheet is a deliberate,
      // normal action, not a failure — both iOS and Android's Share plugin
      // reject with this exact, undocumented-but-stable string on cancel
      // (verified against @capacitor/share's own source for both
      // platforms), so it's the only reliable way to tell "canceled" apart
      // from a real write/share failure worth surfacing to the user.
      if (e instanceof Error && e.message === "Share canceled") return;
      throw e;
    }
    return;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Pretty-printed JSON download (backlog LAUNCH-9 full-account export). */
export async function downloadJSON(filename: string, data: unknown): Promise<void> {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8;" });
  await triggerDownload(filename, blob);
}

/**
 * Reads a Blob's bytes as bare base64 (no "data:...;base64," prefix) —
 * Filesystem.writeFile expects base64 for any binary payload (no
 * `encoding` option, unlike triggerDownload's UTF8-text case above, which
 * would corrupt a binary file like a PDF if reused as-is). FileReader lets
 * the browser do the encoding natively rather than a JS loop over every
 * byte, which matters once a real multi-page PDF is a few MB.
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the generated file."));
    reader.readAsDataURL(blob);
  });
}

/**
 * Native (Capacitor WKWebView/Android WebView) path shared by downloadPdf()
 * and previewPdf() below — base64, not UTF8 text, since a PDF is binary.
 * There's no "preview tab" on native, and the OS Share sheet already
 * includes AirPrint/Android print alongside Save-to-Files/email/etc., so
 * one native path genuinely serves both intents (this is also the likely
 * fix for backlog MOBILE-15, "Download / Print PDF produced no iOS
 * print/share sheet on a real device" — window.print()'s near-nonexistent
 * WKWebView support is replaced here for every platform at once, not just
 * desktop Chrome; confirm once verified on a real device).
 */
async function sharePdf(filename: string, blob: Blob): Promise<void> {
  const { uri } = await Filesystem.writeFile({
    path: filename,
    data: await blobToBase64(blob),
    directory: Directory.Cache,
  });
  try {
    await Share.share({ files: [uri] });
  } catch (e) {
    // Same undocumented-but-stable cancel string triggerDownload's own
    // comment already relies on — the user backing out of the native
    // share sheet is a deliberate, normal action, not a failure.
    if (e instanceof Error && e.message === "Share canceled") return;
    throw e;
  }
}

/**
 * Downloads a generated PDF immediately under a controlled filename
 * (backlog PRINT-26 follow-up). Two things were tried and confirmed (via
 * live testing) to have NO effect on what Chrome's built-in PDF viewer
 * names a save: wrapping the blob in a File before creating its object
 * URL, and setting the source page's document.title before Cloudflare
 * renders it (the trick that worked for the old window.print() flow,
 * which doesn't carry over to Cloudflare's printToPDF call). The `<a
 * download>` trick below is the only mechanism actually proven to control
 * a Blob's saved filename in this codebase — the same one triggerDownload()
 * already uses for CSV/JSON. It can't be combined with a preview: the
 * `download` attribute tells the browser to save immediately rather than
 * navigate/render, which is exactly why downloading and previewing are two
 * separate actions now (see previewPdf() below) instead of one.
 */
export async function downloadPdf(filename: string, blob: Blob): Promise<void> {
  if (Capacitor.isNativePlatform()) return sharePdf(filename, blob);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Opens a blank tab synchronously — must be called directly inside the
 * click handler, before any `await`. Confirmed live (backlog PRINT-26):
 * calling window.open() only AFTER awaiting the multi-second Cloudflare PDF
 * generation gets silently popup-blocked, because the browser's "trusted
 * user activation" window (tied to the original click) has already expired
 * by then — a real "Pop-ups blocked" prompt, not a bug in the PDF itself,
 * and timing-dependent (worked when generation happened to finish fast,
 * failed when it didn't). Opening the tab immediately, before any async
 * work, keeps the open() call inside the trusted click every time. No tab
 * to pre-open on native. The loading message avoids a jarring blank tab
 * while Cloudflare renders.
 */
export function openPreviewTab(): Window | null {
  if (Capacitor.isNativePlatform()) return null;
  const w = window.open("", "_blank");
  w?.document.write(
    "<title>Generating…</title><body style='font-family:system-ui,sans-serif;padding:24px;color:#555'>Generating your PDF…</body>",
  );
  return w;
}

/**
 * Shows a generated PDF in a tab already opened via openPreviewTab() — the
 * browser's own PDF viewer takes over from there, with its own print button
 * (a completely different, reliable code path vs. the old
 * window.print()-on-live-HTML flow PRINT-25 root-caused as silently
 * dropping content on long documents). Saving from inside that viewer
 * instead of printing still gets a browser-assigned filename, not
 * `filename` below — downloadPdf() above is the path for a guaranteed one.
 * Falls back to a fresh window.open() if the tab from openPreviewTab() is
 * missing or was closed — better than silently doing nothing, even though
 * that fallback can itself get popup-blocked by this point.
 */
export async function previewPdf(tabWindow: Window | null, filename: string, blob: Blob): Promise<void> {
  if (Capacitor.isNativePlatform()) return sharePdf(filename, blob);
  const url = URL.createObjectURL(blob);
  if (tabWindow && !tabWindow.closed) {
    tabWindow.location.href = url;
  } else {
    window.open(url, "_blank");
  }
}
